import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Hero } from '../components/Hero';
import { ClipboardCheckIcon, ActivityIcon, TreesIcon, MapIcon, MapPinIcon, ArrowRightIcon, CheckCircle2Icon, FuelIcon, ZapIcon, BuildingIcon, PlaneIcon, WifiIcon, FactoryIcon, WheatIcon, LandmarkIcon, GlobeIcon } from 'lucide-react';
interface HomePageProps {
  onNavigate: (page: string) => void;
}
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function HomePage({
  onNavigate
}: HomePageProps) {
  const handleNavClick = (page: string) => {
    onNavigate(page);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="bg-gesl-offWhite" data-id="element-503">
      <Hero onNavigate={onNavigate} data-id="element-504" />

      {/* Stats Overlap Section */}

      {/* About Preview Section */}
      <section className="py-24 lg:py-32 bg-white" data-id="element-505">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-506">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-507">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="relative" data-id="element-508">
              <div className="absolute -inset-4 bg-gesl-warmWhite rounded-sm transform -rotate-2 -z-10" data-id="element-509" />
              <img src="https://cdn.magicpatterns.com/uploads/ogA89136ZE7ystXWY2ZF5s/pasted-image.png" alt="GESL team conducting soil sampling" className="w-full h-auto rounded-sm shadow-lg object-cover aspect-[4/3]" data-id="element-510" />
              <div className="absolute -bottom-6 -right-6 bg-gesl-dark text-white p-6 rounded-sm shadow-xl hidden sm:block" data-id="element-511">
                <div className="text-4xl font-bold text-gesl-gold mb-1" data-id="element-512">
                  2000
                </div>
                <div className="text-sm font-medium tracking-wider uppercase" data-id="element-513">
                  Incorporated
                </div>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="lg:pl-8" data-id="element-514">
              <motion.div variants={fadeUpVariants} className="flex items-center gap-3 mb-4" data-id="element-515">
                <div className="h-px w-8 bg-gesl-gold" data-id="element-516" />
                <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-517">
                  Who We Are
                </span>
              </motion.div>

              <motion.h2 variants={fadeUpVariants} className="text-3xl md:text-4xl font-bold text-gesl-dark mb-6 leading-tight" data-id="element-518">
                Nigeria's Premier Environmental Consultancy
              </motion.h2>

              <motion.p variants={fadeUpVariants} className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-519">
                Greenwater Environmental Services Limited (GESL) is a wholly
                Nigerian-owned multidisciplinary environmental management
                company. We promote sustainable management of environmental
                resources and provide scientific solutions to complex
                environmental challenges.
              </motion.p>

              <motion.ul variants={fadeUpVariants} className="space-y-4 mb-10" data-id="element-520">
                {['30+ Years Industry Experience', 'CAC Registered (RC 377939)', 'NESREA & FMEnv Accredited'].map((feature, idx) => <li key={idx} className="flex items-center text-gesl-charcoal font-medium" data-id="element-521">
                    <CheckCircle2Icon className="h-5 w-5 text-gesl-green mr-3 flex-shrink-0" data-id="element-522" />
                    {feature}
                  </li>)}
              </motion.ul>

              <motion.button variants={fadeUpVariants} onClick={() => handleNavClick('about')} className="group flex items-center gap-2 px-6 py-3 bg-gesl-dark text-white font-semibold rounded-sm hover:bg-gesl-green transition-colors duration-300" data-id="element-523">
                Learn More About Us
                <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-524" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-24 lg:py-32 bg-gesl-warmWhite" data-id="element-525">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-526">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="text-center max-w-3xl mx-auto mb-16" data-id="element-527">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-528">
              <div className="h-px w-8 bg-gesl-gold" data-id="element-529" />
              <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-530">
                What We Do
              </span>
              <div className="h-px w-8 bg-gesl-gold" data-id="element-531" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gesl-dark mb-6" data-id="element-532">
              Comprehensive Environmental Solutions
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-533">
            {[{
            icon: ClipboardCheckIcon,
            title: 'Environmental Assessment',
            desc: 'EIA, Environmental Audits, and Site Assessments for regulatory compliance.'
          }, {
            icon: ActivityIcon,
            title: 'Environmental Monitoring',
            desc: 'Compliance monitoring and pollution control studies for ongoing operations.'
          }, {
            icon: TreesIcon,
            title: 'Sustainability Advisory',
            desc: 'ESG advisory, climate change studies, and stakeholder engagement.'
          }, {
            icon: MapIcon,
            title: 'Project Development',
            desc: 'Resettlement Action Plans and Environmental Management Plans.'
          }].map((service, idx) => <motion.div key={idx} variants={fadeUpVariants} className="bg-white p-8 rounded-sm shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 group flex flex-col h-full" data-id="element-534">
                <div className="h-12 w-12 bg-gesl-offWhite rounded-sm flex items-center justify-center mb-6 group-hover:bg-gesl-dark transition-colors duration-300" data-id="element-535">
                  <service.icon className="h-6 w-6 text-gesl-green group-hover:text-gesl-gold transition-colors duration-300" data-id="element-536" />
                </div>
                <h3 className="text-xl font-bold text-gesl-dark mb-3" data-id="element-537">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow" data-id="element-538">
                  {service.desc}
                </p>
                <button onClick={() => handleNavClick('services')} className="text-gesl-green font-semibold text-sm flex items-center gap-1 group-hover:text-gesl-gold transition-colors mt-auto w-fit" data-id="element-539">
                  Learn More <ArrowRightIcon className="h-4 w-4" data-id="element-540" />
                </button>
              </motion.div>)}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUpVariants} className="text-center" data-id="element-541">
            <button onClick={() => handleNavClick('services')} className="inline-flex items-center justify-center px-8 py-4 border-2 border-gesl-dark text-gesl-dark font-bold rounded-sm hover:bg-gesl-dark hover:text-white transition-colors duration-300" data-id="element-542">
              View All Services
            </button>
          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-24 lg:py-32 bg-white" data-id="element-543">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-544">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16" data-id="element-545">
            <div data-id="element-546">
              <div className="flex items-center gap-3 mb-4" data-id="element-547">
                <div className="h-px w-8 bg-gesl-gold" data-id="element-548" />
                <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-549">
                  Showcase
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gesl-dark" data-id="element-550">
                Featured Projects
              </h2>
            </div>
            <button onClick={() => handleNavClick('projects')} className="hidden md:flex items-center gap-2 text-gesl-green font-semibold hover:text-gesl-gold transition-colors" data-id="element-551">
              View All Projects <ArrowRightIcon className="h-4 w-4" data-id="element-552" />
            </button>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-553">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/5sgSExmnQE5D16JSGvQCxe/image67.jpg',
            title: '20MW Solar Power Projects',
            loc: 'Borno & Katsina States',
            cat: 'Power & Energy'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/9m1CdMZikpdKWDi2tUE4XK/image59.jpg',
            title: 'Coastal Water Quality Monitoring',
            loc: 'Niger Delta Region',
            cat: 'Oil & Gas'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/1mSuDuJ28asGEhrmTLFgWM/image61.jpg',
            title: 'Shoreline Environmental Assessment',
            loc: 'Coastal Nigeria',
            cat: 'Environmental Monitoring'
          }].map((project, idx) => <motion.div key={idx} variants={fadeUpVariants} className="group bg-white rounded-sm overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col" data-id="element-554">
                <div className="relative h-60 overflow-hidden" data-id="element-555">
                  <div className="absolute inset-0 bg-gesl-dark/10 group-hover:bg-transparent transition-colors duration-300 z-10" data-id="element-556" />
                  <img src={project.img} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-557" />
                  <div className="absolute top-4 left-4 z-20" data-id="element-558">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gesl-dark text-xs font-bold uppercase tracking-wider rounded-sm shadow-sm" data-id="element-559">
                      {project.cat}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex-grow flex flex-col" data-id="element-560">
                  <h3 className="text-xl font-bold text-gesl-dark mb-3 group-hover:text-gesl-green transition-colors" data-id="element-561">
                    {project.title}
                  </h3>
                  <div className="flex items-center text-gray-500 text-sm font-medium mt-auto" data-id="element-562">
                    <MapPinIcon className="h-4 w-4 mr-2 text-gesl-gold" data-id="element-563" />
                    {project.loc}
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          <div className="mt-10 text-center md:hidden" data-id="element-564">
            <button onClick={() => handleNavClick('projects')} className="inline-flex items-center gap-2 text-gesl-green font-semibold hover:text-gesl-gold transition-colors" data-id="element-565">
              View All Projects <ArrowRightIcon className="h-4 w-4" data-id="element-566" />
            </button>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 bg-gesl-offWhite border-y border-gray-200/50" data-id="element-567">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-568">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="text-center mb-12" data-id="element-569">
            <h2 className="text-3xl font-bold text-gesl-dark mb-4" data-id="element-570">
              Industries We Serve
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-571">
              Delivering specialized environmental solutions across diverse
              sectors of the Nigerian economy.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-id="element-572">
            {[{
            name: 'Oil & Gas',
            icon: FuelIcon
          }, {
            name: 'Power',
            icon: ZapIcon
          }, {
            name: 'Infrastructure',
            icon: BuildingIcon
          }, {
            name: 'Aviation',
            icon: PlaneIcon
          }, {
            name: 'Telecom',
            icon: WifiIcon
          }, {
            name: 'Manufacturing',
            icon: FactoryIcon
          }, {
            name: 'Agriculture',
            icon: WheatIcon
          }, {
            name: 'Government',
            icon: LandmarkIcon
          }, {
            name: 'Intl. Development',
            icon: GlobeIcon
          }].map((ind, idx) => <motion.div key={idx} variants={fadeUpVariants} className="bg-white p-4 rounded-sm border border-gray-100 shadow-sm flex items-center gap-3 hover:border-gesl-green hover:shadow-md transition-all cursor-default" data-id="element-573">
                <div className="h-10 w-10 bg-gesl-warmWhite rounded-sm flex items-center justify-center flex-shrink-0" data-id="element-574">
                  <ind.icon className="h-5 w-5 text-gesl-green" data-id="element-575" />
                </div>
                <span className="font-semibold text-sm text-gesl-charcoal leading-tight" data-id="element-576">
                  {ind.name}
                </span>
              </motion.div>)}

            <motion.button variants={fadeUpVariants} onClick={() => handleNavClick('industries')} className="bg-gesl-dark p-4 rounded-sm shadow-sm flex items-center justify-center gap-2 hover:bg-gesl-green transition-colors group col-span-2 sm:col-span-1" data-id="element-577">
              <span className="font-semibold text-sm text-white" data-id="element-578">
                Explore All
              </span>
              <ArrowRightIcon className="h-4 w-4 text-gesl-gold group-hover:translate-x-1 transition-transform" data-id="element-579" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 lg:py-32 bg-gesl-dark text-white overflow-hidden relative" data-id="element-580">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+CjxwYXRoIGQ9Ik01NC42MjcgMjAuMjc2TDU0LjYyNyAyMC4yNzZMNjAgMjUuNjQ5TDU0LjYyNyAzMS4wMjJMNDkuMjU1IDI1LjY0OUw1NC42MjcgMjAuMjc2WiIgZmlsbD0iIzFhNWMzYSIgZmlsbC1vcGFjaXR5PSIwLjA1Ii8+Cjwvc3ZnPg==')] opacity-20" data-id="element-581" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-582">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-583">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="order-2 lg:order-1 relative" data-id="element-584">
              <div className="absolute inset-0 bg-gesl-green transform translate-x-4 translate-y-4 rounded-sm -z-10" data-id="element-585" />
              <img src="https://cdn.magicpatterns.com/uploads/ofTddE5qE7it8RhpohVM89/pasted-image.png" alt="GESL team group photo" className="w-full h-auto rounded-sm shadow-2xl object-cover aspect-[4/3]" data-id="element-586" />
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="order-1 lg:order-2" data-id="element-587">
              <motion.div variants={fadeUpVariants} className="flex items-center gap-3 mb-4" data-id="element-588">
                <div className="h-px w-8 bg-gesl-gold" data-id="element-589" />
                <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-590">
                  Competitive Advantage
                </span>
              </motion.div>

              <motion.h2 variants={fadeUpVariants} className="text-3xl md:text-4xl font-bold mb-10 leading-tight" data-id="element-591">
                Why Choose GESL
              </motion.h2>

              <motion.div variants={staggerContainer} className="space-y-6" data-id="element-592">
                {['Over 30 years of industry experience and proven delivery record', 'Strong regulatory interface with FMEnv, NESREA, DPR, NOSDRA', 'Nationwide project footprint across all geopolitical zones', 'Multidisciplinary technical team with deep sector expertise', 'Strong stakeholder management and community engagement capacity', 'Cost-effective, science-based solutions tailored to your needs'].map((adv, idx) => <motion.div key={idx} variants={fadeUpVariants} className="flex items-start" data-id="element-593">
                    <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0 mt-0.5" data-id="element-594">
                      <CheckCircle2Icon className="h-5 w-5 text-gesl-gold" data-id="element-595" />
                    </div>
                    <p className="text-white/90 text-lg leading-relaxed" data-id="element-596">
                      {adv}
                    </p>
                  </motion.div>)}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Banner Section */}
      <section className="py-20 bg-gradient-to-br from-gesl-green to-gesl-dark relative overflow-hidden" data-id="element-597">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gesl-gold/10 rounded-full blur-3xl" data-id="element-598" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" data-id="element-599" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-600">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUpVariants} data-id="element-601">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight" data-id="element-602">
              Ready to Partner With Us?
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto font-light" data-id="element-603">
              Get in touch for a consultation on your next environmental
              project. Our team of experts is ready to deliver sustainable
              solutions.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-604">
              <button onClick={() => handleNavClick('contact')} className="px-8 py-4 bg-gesl-gold text-white font-bold rounded-sm hover:bg-white hover:text-gesl-dark transition-colors duration-300 shadow-lg" data-id="element-605">
                Contact Us Today
              </button>
              <button onClick={() => handleNavClick('services')} className="px-8 py-4 border-2 border-white/30 text-white font-bold rounded-sm hover:bg-white/10 hover:border-white/50 transition-colors duration-300" data-id="element-606">
                View Our Services
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}